import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FaArchive, FaArrowDown, FaArrowLeft, FaArrowRight, FaArrowUp, FaBell,
    FaCalendarAlt, FaCheck, FaCheckSquare, FaChevronDown, FaClock, FaColumns, FaCopy,
    FaDownload, FaEdit, FaExchangeAlt, FaExternalLinkAlt, FaEye, FaFileAlt,
    FaPaperclip, FaPlus,
    FaRegCircle, FaRegStar, FaStar, FaStopwatch, FaTrash, FaUpload, FaChartBar, FaSlidersH,
} from "react-icons/fa";
import {
    KANBAN_COLUMNS_CONSTANTS, KANBAN_CARDS_CONSTANTS, KANBAN_CARD_FILES_CONSTANTS,
} from "../_MODULES/_API";
import { AppKanban, useKanban } from "../../../../../_CORE/APP/Templates/_UI/Kanban/_index";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Badge, EmptyState, Skeleton } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import {
    FieldDate, FieldFile, FieldInput, FieldSelect, FieldSwitch, FieldTags,
    FieldTextarea, FormGrid, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { AppPopover, PopoverItem, PopoverTitle } from "../../../../../_CORE/APP/Templates/_UI/Popover/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { UniversalFilePreviewModal } from "../../../../_UI/UniversalFilePreview";
import { downloadFilesAsZip } from "../../../../_UI/FileBatchDownload";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { onApiAction } from "../../../../_UI/onApiAction";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../../../../_CORE/HELPERS/reactHotToast.helper";

const PRIORITIES = [
    { value: "URGENT", label: "Urgente" },
    { value: "HIGH", label: "Alta" },
    { value: "MEDIUM", label: "Media" },
    { value: "LOW", label: "Baja" },
];

const PRIORITY_TONE = { URGENT: "danger", HIGH: "warning", MEDIUM: "neutral", LOW: "neutral" };

const COLUMN_EMPTY = {
    name: "", description: "", wipLimit: "", isDoneColumn: false,
};

const CARD_EMPTY = {
    columnId: "", title: "", description: "", priority: "MEDIUM", labels: [], checklist: [],
    startDate: null, dueDate: null, reminderAt: null, estimateMinutes: "", actualMinutes: "",
    url: "", notes: "", isFavorite: false,
};

const EXPORT_COLUMNS = [
    { key: "column", label: "Columna" },
    { key: "order", label: "Orden" },
    { key: "title", label: "Tarjeta" },
    { key: "priority", label: "Prioridad" },
    { key: "startDate", label: "Inicio" },
    { key: "dueDate", label: "Vencimiento" },
    { key: "reminderAt", label: "Recordatorio" },
    { key: "estimate", label: "Estimado" },
    { key: "actual", label: "Real" },
    { key: "labels", label: "Etiquetas" },
    { key: "checklist", label: "Checklist" },
    { key: "attachments", label: "Adjuntos" },
    { key: "description", label: "Descripción" },
    { key: "url", label: "Enlace" },
    { key: "notes", label: "Notas" },
];

const onAsArray = (value) => Array.isArray(value) ? value : [];
const onLabel = (options, value, fallback = "") => options.find((item) => item.value === value)?.label || fallback || value;
const onSafeName = (value) => String(value || "tablero").trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "tablero";
const onDateObject = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const date = new Date(String(value).replace(" ", "T"));
    return Number.isNaN(date.getTime()) ? null : date;
};
const onDateLabel = (value) => {
    const date = onDateObject(value);
    return date ? date.toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" }) : "";
};
const onDateTimeLabel = (value) => {
    const date = onDateObject(value);
    return date ? date.toLocaleString("es-AR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
};
const onChecklistProgress = (items) => {
    const all = onAsArray(items);
    const done = all.filter((item) => !!item.done).length;
    return { done, total: all.length, percent: all.length ? Math.round((done / all.length) * 100) : 0 };
};
const onIsOverdue = (card, doneColumnIds) => {
    const date = onDateObject(card?.dueDate);
    return !!date && date.getTime() < Date.now() && !doneColumnIds.has(String(card?.columnId));
};
const onIsReminderDue = (card, doneColumnIds) => {
    const date = onDateObject(card?.reminderAt);
    return !!date && date.getTime() <= Date.now() && !doneColumnIds.has(String(card?.columnId));
};
const onChecklistId = () => window.crypto?.randomUUID?.() || `check_${Date.now()}_${Math.random().toString(36).slice(2)}`;
const onFormatMinutes = (value) => {
    const minutes = Number(value || 0);
    if (!Number.isFinite(minutes) || minutes <= 0) return "";
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (!hours) return `${rest} min`;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
};
const onFormatSize = (value) => {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
};
const onResponseData = (response) => response?.data?.data ?? response?.data ?? response ?? null;
const onIsApiFailure = (response) => response?.success === false || Number(response?.status ?? response?.code_status ?? 200) >= 400;
const onResolveEndpoint = (constant, params = {}) => {
    let endpoint = API_CORE.ENDPOINTS[constant] || "";
    Object.entries(params).forEach(([key, value]) => { endpoint = endpoint.replace(`:${key}`, String(value)); });
    return endpoint;
};

const TrelloMoveControl = ({ card, columns, onMove, isWide = false }) => {
    const [isOpen, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const current = columns.find((column) => String(column.id) === String(card?.columnId));

    return (
        <div className="LStudio__app_trello_move">
            <button
                ref={anchorRef}
                type="button"
                className="LStudio__app_trello_move_trigger"
                data-wide={isWide ? "true" : undefined}
                onClick={() => setOpen((value) => !value)}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                title={isWide ? "Cambiar columna" : `Mover tarjeta · ${current?.label || "Columna"}`}
            >
                <FaExchangeAlt />
                {isWide && <span>{current?.label || "Mover"}</span>}
                <FaChevronDown className="LStudio__app_trello_move_chevron" />
            </button>
            <AppPopover isOpen={isOpen} onClose={() => setOpen(false)} anchorRef={anchorRef} placement="bottom" align="end" width={230} role="menu"
                className={`LStudio__app_trello_move_popover${isWide ? " LStudio__app_trello_move_popover--modal" : ""}`}>
                <PopoverTitle>Mover a otra columna</PopoverTitle>
                {columns.map((column) => {
                    const selected = String(column.id) === String(card?.columnId);
                    return (
                        <PopoverItem
                            key={column.id}
                            icon={selected ? <FaCheck /> : <FaColumns />}
                            isDisabled={selected}
                            onClick={() => { setOpen(false); onMove?.(card, column.id); }}
                        >
                            {column.label}
                        </PopoverItem>
                    );
                })}
            </AppPopover>
        </div>
    );
};

export const PanelTrello = ({ projectId, projectName = "Proyecto" }) => {
    const [useSearch, setSearch] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [usePriorityFilter, setPriorityFilter] = useState("ALL");
    const [useDueFilter, setDueFilter] = useState("ALL");
    const [useColumnFilter, setColumnFilter] = useState("ALL");
    const [useColumnModal, setColumnModal] = useState(false);
    const [useColumnEdit, setColumnEdit] = useState(null);
    const [useCardModal, setCardModal] = useState(false);
    const [useCardEdit, setCardEdit] = useState(null);
    const [useChecklistDraft, setChecklistDraft] = useState("");
    const [usePendingFiles, setPendingFiles] = useState([]);
    const [useSavingCard, setSavingCard] = useState(false);

    const [useCardView, setCardView] = useState(null);
    const [useCardFiles, setCardFiles] = useState([]);
    const [useCardFilesLoading, setCardFilesLoading] = useState(false);
    const [usePreviewUploadFiles, setPreviewUploadFiles] = useState([]);
    const [usePreviewUploading, setPreviewUploading] = useState(false);
    const [usePreviewFile, setPreviewFile] = useState(null);
    const [useDownloadingFiles, setDownloadingFiles] = useState(false);

    const {
        apiGet: onGetColumns, useApiData: COLUMNS, useApiLoading: COLUMNS_LOADING, mutateLocal: mutateColumns,
    } = useApiQuery({ minTimeQuery: 140, enablePagination: false, startLoading: true });
    const {
        apiGet: onGetCards, useApiData: CARDS, useApiLoading: CARDS_LOADING, mutateLocal: mutateCards,
    } = useApiQuery({ minTimeQuery: 140, enablePagination: false, startLoading: true });
    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 140,
    });

    const onLoadColumns = useCallback(() => onGetColumns({
        endpoint: KANBAN_COLUMNS_CONSTANTS.BY_PROJECT,
        config: { pathParams: { projectId } },
    }), [onGetColumns, projectId]);
    const onLoadCards = useCallback(() => onGetCards({
        endpoint: KANBAN_CARDS_CONSTANTS.BY_PROJECT,
        config: { pathParams: { projectId } },
    }), [onGetCards, projectId]);

    useEffect(() => {
        if (!projectId) return;
        onLoadColumns();
        onLoadCards();
    }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const columns = useMemo(() => onAsArray(COLUMNS)
        .slice().sort((a, b) => Number(a.position || 0) - Number(b.position || 0)), [COLUMNS]);
    const cards = useMemo(() => onAsArray(CARDS), [CARDS]);
    const doneColumnIds = useMemo(() => new Set(columns.filter((column) => column.isDoneColumn).map((column) => String(column.id))), [columns]);

    const board = useKanban({
        cards: [],
        onMove: async ({ card, toColumnId, order }) => {
            const response = await onSilentPatch({
                endpoint: KANBAN_CARDS_CONSTANTS.UPDATE,
                id: card.id,
                data: { columnId: toColumnId, position: order },
                errorMessage: "No se pudo mover la tarjeta.",
            });
            if (!response.success) throw new Error("MOVE_FAILED");
            mutateCards({ id: card.id, patch: { columnId: toColumnId, position: order } });
            setCardView((current) => current?.id === card.id ? { ...current, columnId: toColumnId, position: order } : current);
        },
    });

    useEffect(() => {
        board.setCards(cards.map((card) => ({ ...card, order: Number(card.position || 0) })));
    }, [cards]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!useCardView?.id) return;
        const latest = cards.find((card) => card.id === useCardView.id);
        if (latest) setCardView((current) => ({ ...current, ...latest }));
    }, [cards, useCardView?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const onLoadCardFiles = useCallback(async (cardId) => {
        if (!cardId) return [];
        setCardFilesLoading(true);
        try {
            const response = await API_CORE.SERVICES.GET_RESOURCE(onResolveEndpoint(KANBAN_CARDS_CONSTANTS.FILES, { id: cardId }));
            if (onIsApiFailure(response)) {
                REACT_HOT_TOAST_ERROR(response?.message || "No se pudieron cargar los adjuntos");
                setCardFiles([]);
                return [];
            }
            const data = onResponseData(response);
            const files = Array.isArray(data) ? data : [];
            setCardFiles(files);
            mutateCards({ id: cardId, patch: { attachmentCount: files.length } });
            return files;
        } finally {
            setCardFilesLoading(false);
        }
    }, [mutateCards]);

    const onUploadFilesToCard = useCallback(async (cardId, files, { updatePreview = false } = {}) => {
        if (!cardId || !files?.length) return [];
        const formData = new FormData();
        files.forEach((file) => formData.append("files[]", file));
        const response = await API_CORE.SERVICES.POST_RESOURCE(
            onResolveEndpoint(KANBAN_CARDS_CONSTANTS.FILE_UPLOAD, { id: cardId }),
            formData,
        );
        if (onIsApiFailure(response)) {
            REACT_HOT_TOAST_ERROR(response?.message || "No se pudieron subir los archivos");
            return [];
        }
        const data = onResponseData(response);
        const saved = Array.isArray(data) ? data : [];
        if (saved.length) {
            if (updatePreview) setCardFiles((current) => [...current, ...saved]);
            mutateCards({ id: cardId, patch: {
                attachmentCount: Math.max(0, Number(cards.find((card) => card.id === cardId)?.attachmentCount || 0)) + saved.length,
            } });
            REACT_HOT_TOAST_SUCCESS(`${saved.length} archivo${saved.length === 1 ? " adjuntado" : "s adjuntados"}`);
        }
        return saved;
    }, [cards, mutateCards]);

    const COLUMN_FORM = useForm({
        initialValues: COLUMN_EMPTY,
        schema: { name: { required: true, minLength: 2 } },
        onSubmit: async (values) => {
            const isEdit = !!useColumnEdit?.id;
            const payload = {
                name: values.name.trim(),
                description: values.description || "",
                wipLimit: values.wipLimit === "" || values.wipLimit == null ? null : Math.max(1, Number(values.wipLimit)),
                isDoneColumn: !!values.isDoneColumn,
            };
            if (!isEdit) payload.projectId = projectId;
            const response = isEdit
                ? await actionMutate({ method: "PATCH", endpoint: KANBAN_COLUMNS_CONSTANTS.UPDATE, id: useColumnEdit.id, data: payload, successMessage: "Columna actualizada" })
                : await actionMutate({ method: "POST", endpoint: KANBAN_COLUMNS_CONSTANTS.CREATE, data: payload, successMessage: "Columna creada" });
            if (!response.success) { COLUMN_FORM.applyApiError(response); return; }
            if (isEdit) mutateColumns({ id: useColumnEdit.id, patch: response.data });
            else mutateColumns({ insert: response.data, position: "end" });
            setColumnModal(false);
            setColumnEdit(null);
            COLUMN_FORM.reset();
        },
    });

    const CARD_FORM = useForm({
        initialValues: CARD_EMPTY,
        schema: { columnId: { required: true }, title: { required: true, minLength: 2 } },
        onSubmit: async (values) => {
            setSavingCard(true);
            try {
                const isEdit = !!useCardEdit?.id;
                const payload = {
                    columnId: values.columnId,
                    title: values.title.trim(),
                    description: values.description || "",
                    priority: values.priority || "MEDIUM",
                    labels: onAsArray(values.labels),
                    checklist: onAsArray(values.checklist),
                    startDate: values.startDate || null,
                    dueDate: values.dueDate || null,
                    reminderAt: values.reminderAt || null,
                    estimateMinutes: values.estimateMinutes === "" || values.estimateMinutes == null ? null : Math.max(0, Number(values.estimateMinutes)),
                    actualMinutes: values.actualMinutes === "" || values.actualMinutes == null ? null : Math.max(0, Number(values.actualMinutes)),
                    url: values.url || "",
                    notes: values.notes || "",
                    isFavorite: !!values.isFavorite,
                };
                if (!isEdit) payload.projectId = projectId;
                const response = isEdit
                    ? await actionMutate({ method: "PATCH", endpoint: KANBAN_CARDS_CONSTANTS.UPDATE, id: useCardEdit.id, data: payload, successMessage: "Tarjeta actualizada" })
                    : await actionMutate({ method: "POST", endpoint: KANBAN_CARDS_CONSTANTS.CREATE, data: payload, successMessage: "Tarjeta creada" });
                if (!response.success) { CARD_FORM.applyApiError(response); return; }

                const savedCard = response.data;
                if (isEdit) mutateCards({ id: useCardEdit.id, patch: savedCard });
                else mutateCards({ insert: { ...savedCard, attachmentCount: 0 }, position: "end" });

                if (usePendingFiles.length) {
                    await onUploadFilesToCard(savedCard.id, usePendingFiles);
                }

                setCardModal(false);
                setCardEdit(null);
                setChecklistDraft("");
                setPendingFiles([]);
                setCardFiles([]);
                CARD_FORM.reset();
            } finally {
                setSavingCard(false);
            }
        },
    });

    const columnOptions = columns.map((column) => ({ value: column.id, label: column.name }));
    const kanbanColumns = columns.map((column) => ({
        id: column.id,
        label: column.name,
        description: column.description,
        limit: column.wipLimit ? Number(column.wipLimit) : null,
        tone: column.isDoneColumn ? "success" : "neutral",
        raw: column,
    }));

    const onOpenCreateColumn = () => {
        setColumnEdit(null);
        COLUMN_FORM.setValues(COLUMN_EMPTY);
        setColumnModal(true);
    };
    const onOpenEditColumn = (column) => {
        setColumnEdit(column);
        COLUMN_FORM.setValues({
            name: column.name || "", description: column.description || "",
            wipLimit: column.wipLimit ?? "", isDoneColumn: !!column.isDoneColumn,
        });
        setColumnModal(true);
    };
    const onOpenCreateCard = (column = columns[0]) => {
        if (!column?.id) {
            REACT_HOT_TOAST_ERROR("Creá una columna antes de agregar tarjetas");
            return;
        }
        setCardEdit(null);
        CARD_FORM.setValues({ ...CARD_EMPTY, columnId: column.id });
        setChecklistDraft("");
        setPendingFiles([]);
        setCardModal(true);
    };
    const onOpenEditCard = (card) => {
        setCardEdit(card);
        setCardFiles([]);
        onLoadCardFiles(card.id);
        CARD_FORM.setValues({
            columnId: card.columnId || columns[0]?.id || "",
            title: card.title || "", description: card.description || "", priority: card.priority || "MEDIUM",
            labels: onAsArray(card.labels), checklist: onAsArray(card.checklist),
            startDate: onDateObject(card.startDate), dueDate: onDateObject(card.dueDate), reminderAt: onDateObject(card.reminderAt),
            estimateMinutes: card.estimateMinutes ?? "", actualMinutes: card.actualMinutes ?? "",
            url: card.url || "", notes: card.notes || "", isFavorite: !!card.isFavorite,
        });
        setChecklistDraft("");
        setPendingFiles([]);
        setCardModal(true);
    };
    const onCloseCardEditor = () => {
        setCardModal(false);
        setCardEdit(null);
        setPendingFiles([]);
        setCardFiles([]);
        setPreviewFile(null);
    };
    const onOpenCardPreview = async (card) => {
        setCardView(card);
        setPreviewUploadFiles([]);
        setCardFiles([]);
        await onLoadCardFiles(card.id);
    };
    const onCloseCardPreview = () => {
        setCardView(null);
        setCardFiles([]);
        setPreviewUploadFiles([]);
    };

    const onMoveCardFromControl = async (card, columnId) => {
        if (!card?.id || !columnId || String(card.columnId) === String(columnId)) return;
        const moved = await board.onMoveCard(card.id, columnId);
        if (!moved) REACT_HOT_TOAST_ERROR("No se pudo mover la tarjeta");
    };

    const onMoveColumn = async (column, direction) => {
        const index = columns.findIndex((item) => item.id === column.id);
        const targetIndex = index + direction;
        if (index < 0 || targetIndex < 0 || targetIndex >= columns.length) return;
        const other = columns[targetIndex];
        const aPosition = Number(column.position || index);
        const bPosition = Number(other.position || targetIndex);
        mutateColumns({ id: column.id, patch: { position: bPosition } });
        mutateColumns({ id: other.id, patch: { position: aPosition } });
        const [a, b] = await Promise.all([
            onSilentPatch({ endpoint: KANBAN_COLUMNS_CONSTANTS.UPDATE, id: column.id, data: { position: bPosition }, errorMessage: "No se pudo reordenar la columna." }),
            onSilentPatch({ endpoint: KANBAN_COLUMNS_CONSTANTS.UPDATE, id: other.id, data: { position: aPosition }, errorMessage: "No se pudo reordenar la columna." }),
        ]);
        if (!a.success || !b.success) onLoadColumns();
    };

    const onToggleFavoriteCard = async (card) => {
        const next = !card.isFavorite;
        mutateCards({ id: card.id, patch: { isFavorite: next } });
        setCardView((current) => current?.id === card.id ? { ...current, isFavorite: next } : current);
        const response = await onSilentPatch({ endpoint: KANBAN_CARDS_CONSTANTS.UPDATE, id: card.id, data: { isFavorite: next }, errorMessage: "No se pudo actualizar el destacado." });
        if (!response.success) {
            mutateCards({ id: card.id, patch: { isFavorite: !next } });
            setCardView((current) => current?.id === card.id ? { ...current, isFavorite: !next } : current);
        }
    };

    const onDuplicateCard = async (card) => {
        const response = await actionMutate({
            method: "POST", endpoint: KANBAN_CARDS_CONSTANTS.CREATE,
            data: {
                projectId, columnId: card.columnId, title: `${card.title} (copia)`, description: card.description || "",
                priority: card.priority || "MEDIUM", labels: onAsArray(card.labels),
                checklist: onAsArray(card.checklist).map((item) => ({ ...item, id: onChecklistId(), done: false })),
                startDate: card.startDate || null, dueDate: card.dueDate || null, reminderAt: card.reminderAt || null,
                estimateMinutes: card.estimateMinutes ?? null, actualMinutes: null,
                url: card.url || "", notes: card.notes || "", isFavorite: false,
            },
            successMessage: "Tarjeta duplicada",
        });
        if (response.success) mutateCards({ insert: { ...response.data, attachmentCount: 0 }, position: "end" });
    };

    const onArchiveCard = async (card) => {
        const ok = await confirm.danger("Archivar tarjeta", `Se archivará “${card.title}”. Sus adjuntos se conservan. Podés recuperarla desde Archivados.`);
        if (!ok) return;
        const response = await onApiAction({ method: "PATCH", endpoint: KANBAN_CARDS_CONSTANTS.ARCHIVE_ONE, pathParams: { id: card.id }, successMessage: "Tarjeta archivada" });
        if (response.success) {
            mutateCards({ id: card.id, remove: true });
            if (useCardView?.id === card.id) onCloseCardPreview();
        }
    };

    const onArchiveColumn = async (column) => {
        const count = cards.filter((card) => card.columnId === column.id).length;
        const ok = await confirm.danger("Archivar columna", `Se archivará “${column.name}”${count ? ` junto con ${count} tarjeta${count === 1 ? "" : "s"}` : ""}. Podés recuperarla desde Archivados.`);
        if (!ok) return;
        const response = await onApiAction({ method: "PATCH", endpoint: KANBAN_COLUMNS_CONSTANTS.ARCHIVE_ONE, pathParams: { id: column.id }, successMessage: "Columna archivada" });
        if (response.success) {
            mutateColumns({ id: column.id, remove: true });
            cards.filter((card) => card.columnId === column.id).forEach((card) => mutateCards({ id: card.id, remove: true }));
        }
    };

    const onAddChecklistItem = () => {
        const text = useChecklistDraft.trim();
        if (!text) return;
        CARD_FORM.setValue("checklist", [...onAsArray(CARD_FORM.values.checklist), { id: onChecklistId(), text, done: false }]);
        setChecklistDraft("");
    };
    const onRemoveChecklistItem = (id) => CARD_FORM.setValue("checklist", onAsArray(CARD_FORM.values.checklist).filter((item) => item.id !== id));
    const onChangeChecklistText = (id, text) => CARD_FORM.setValue("checklist", onAsArray(CARD_FORM.values.checklist)
        .map((item) => item.id === id ? { ...item, text } : item));
    const onMoveChecklistItem = (id, direction) => {
        const items = [...onAsArray(CARD_FORM.values.checklist)];
        const index = items.findIndex((item) => item.id === id);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= items.length) return;
        [items[index], items[target]] = [items[target], items[index]];
        CARD_FORM.setValue("checklist", items);
    };

    const onTogglePreviewChecklistItem = async (id) => {
        if (!useCardView?.id) return;
        const previous = onAsArray(useCardView.checklist);
        const next = previous.map((item) => item.id === id ? { ...item, done: !item.done } : item);
        setCardView((current) => ({ ...current, checklist: next }));
        mutateCards({ id: useCardView.id, patch: { checklist: next } });
        const response = await onSilentPatch({
            endpoint: KANBAN_CARDS_CONSTANTS.UPDATE,
            id: useCardView.id,
            data: { checklist: next },
            errorMessage: "No se pudo actualizar el checklist.",
        });
        if (!response.success) {
            setCardView((current) => ({ ...current, checklist: previous }));
            mutateCards({ id: useCardView.id, patch: { checklist: previous } });
        }
    };

    const onPreviewUpload = async () => {
        if (!useCardView?.id || !usePreviewUploadFiles.length || usePreviewUploading) return;
        setPreviewUploading(true);
        try {
            await onUploadFilesToCard(useCardView.id, usePreviewUploadFiles, { updatePreview: true });
            setPreviewUploadFiles([]);
        } finally {
            setPreviewUploading(false);
        }
    };

    const onLoadAttachmentBlob = useCallback(async (file) => {
        if (!file?.id) throw new Error("Archivo sin identificador");
        const response = await API_CORE.SERVICES.GET_RESOURCE(
            onResolveEndpoint(KANBAN_CARD_FILES_CONSTANTS.CONTENT, { id: file.id }),
            { responseType: "blob" },
        );
        if (!(response instanceof Blob)) throw new Error("No se pudo obtener el archivo");
        return response;
    }, []);

    const onDownloadAttachment = useCallback(async (file) => {
        if (!file?.id) return;
        const blob = await API_CORE.SERVICES.GET_RESOURCE(
            onResolveEndpoint(KANBAN_CARD_FILES_CONSTANTS.DOWNLOAD, { id: file.id }),
            { responseType: "blob" },
        );
        if (!(blob instanceof Blob)) return;
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement("a");
        link.href = url;
        link.download = file.originalName || file.title || "archivo";
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, []);

    const onDownloadAllAttachments = async () => {
        if (!useCardFiles.length || useDownloadingFiles) return;
        setDownloadingFiles(true);
        try {
            await downloadFilesAsZip({
                files: useCardFiles,
                loadBlob: onLoadAttachmentBlob,
                fileName: `${onSafeName(useCardView?.title || useCardEdit?.title || "tarjeta")}-adjuntos.zip`,
                successMessage: "Adjuntos descargados",
                errorMessage: "No se pudieron descargar todos los adjuntos.",
            });
        } finally {
            setDownloadingFiles(false);
        }
    };

    const onArchiveAttachment = async (file) => {
        const ok = await confirm.danger("Archivar adjunto", `Se archivará “${file.originalName || file.title}”. Podés recuperarlo desde Archivados.`);
        if (!ok) return;
        const response = await onApiAction({
            method: "PATCH",
            endpoint: KANBAN_CARD_FILES_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: file.id },
            successMessage: "Adjunto archivado",
        });
        if (response.success) {
            setCardFiles((current) => current.filter((item) => item.id !== file.id));
            const targetCardId = file?.cardId || useCardView?.id || useCardEdit?.id;
            const currentCount = Number(
                cards.find((card) => String(card.id) === String(targetCardId))?.attachmentCount
                ?? useCardView?.attachmentCount
                ?? useCardEdit?.attachmentCount
                ?? useCardFiles.length
            );
            const nextCount = Math.max(0, currentCount - 1);
            if (targetCardId) mutateCards({ id: targetCardId, patch: { attachmentCount: nextCount } });
            setCardView((current) => current && String(current.id) === String(targetCardId) ? { ...current, attachmentCount: nextCount } : current);
            setCardEdit((current) => current && String(current.id) === String(targetCardId) ? { ...current, attachmentCount: nextCount } : current);
        }
    };

    const visibleCards = useMemo(() => board.cards.filter((card) => {
        if (usePriorityFilter !== "ALL" && card.priority !== usePriorityFilter) return false;
        if (useDueFilter === "OVERDUE" && !onIsOverdue(card, doneColumnIds)) return false;
        if (useDueFilter === "NO_DATE" && card.dueDate) return false;
        if (useDueFilter === "WITH_DATE" && !card.dueDate) return false;
        if (useColumnFilter !== "ALL" && String(card.columnId) !== String(useColumnFilter)) return false;
        if (!useSearch.trim()) return true;
        return onMatchesSearch(useSearch, [card.title, card.description, card.notes, card.url, card.priority, ...onAsArray(card.labels)]);
    }), [board.cards, usePriorityFilter, useDueFilter, useColumnFilter, useSearch, doneColumnIds]);

    const advancedCount = [
        usePriorityFilter !== "ALL",
        useDueFilter !== "ALL",
        useColumnFilter !== "ALL",
    ].filter(Boolean).length;

    const visibleIds = useMemo(() => new Set(visibleCards.map((card) => String(card.id))), [visibleCards]);
    const boardView = useMemo(() => ({
        ...board,
        onGetColumnCards: (columnId) => board.onGetColumnCards(columnId).filter((card) => visibleIds.has(String(card.id))),
    }), [board, visibleIds]);

    const exportRows = useMemo(() => columns.flatMap((column) => cards
        .filter((card) => card.columnId === column.id)
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
        .map((card, index) => {
            const progress = onChecklistProgress(card.checklist);
            return {
                column: column.name, order: index + 1, title: card.title || "", priority: onLabel(PRIORITIES, card.priority, "Media"),
                startDate: onDateTimeLabel(card.startDate), dueDate: onDateTimeLabel(card.dueDate), reminderAt: onDateTimeLabel(card.reminderAt),
                estimate: onFormatMinutes(card.estimateMinutes), actual: onFormatMinutes(card.actualMinutes),
                labels: onAsArray(card.labels).join(", "), checklist: progress.total ? `${progress.done}/${progress.total}` : "",
                attachments: Number(card.attachmentCount || 0), description: card.description || "", url: card.url || "", notes: card.notes || "",
            };
        })), [columns, cards]);

    const onDownloadMarkdown = () => {
        const lines = [`# Trello · ${projectName}`, ""];
        columns.forEach((column) => {
            lines.push(`## ${column.name}`, "");
            if (column.description) lines.push(column.description, "");
            const columnCards = cards.filter((card) => card.columnId === column.id)
                .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
            if (!columnCards.length) lines.push("_Sin tarjetas._", "");
            columnCards.forEach((card) => {
                lines.push(`### ${card.title}`, "");
                lines.push(`- Prioridad: ${onLabel(PRIORITIES, card.priority, "Media")}`);
                if (card.startDate) lines.push(`- Inicio: ${onDateTimeLabel(card.startDate)}`);
                if (card.dueDate) lines.push(`- Vencimiento: ${onDateTimeLabel(card.dueDate)}`);
                if (card.reminderAt) lines.push(`- Recordatorio: ${onDateTimeLabel(card.reminderAt)}`);
                if (card.estimateMinutes) lines.push(`- Tiempo estimado: ${onFormatMinutes(card.estimateMinutes)}`);
                if (card.actualMinutes) lines.push(`- Tiempo real: ${onFormatMinutes(card.actualMinutes)}`);
                if (Number(card.attachmentCount || 0)) lines.push(`- Adjuntos: ${Number(card.attachmentCount || 0)}`);
                if (onAsArray(card.labels).length) lines.push(`- Etiquetas: ${onAsArray(card.labels).join(", ")}`);
                if (card.description) lines.push("", card.description);
                if (onAsArray(card.checklist).length) {
                    lines.push("", "#### Checklist", "");
                    onAsArray(card.checklist).forEach((item) => lines.push(`- [${item.done ? "x" : " "}] ${item.text}`));
                }
                if (card.url) lines.push("", `Enlace: ${card.url}`);
                if (card.notes) lines.push("", "#### Notas", "", card.notes);
                lines.push("");
            });
        });
        const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `trello-${onSafeName(projectName)}.md`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        REACT_HOT_TOAST_SUCCESS("Tablero Markdown descargado");
    };

    const doneCards = cards.filter((card) => doneColumnIds.has(String(card.columnId))).length;
    const overdueCards = cards.filter((card) => onIsOverdue(card, doneColumnIds)).length;
    const totalChecklist = cards.reduce((acc, card) => acc + onAsArray(card.checklist).length, 0);
    const completedChecklist = cards.reduce((acc, card) => acc + onAsArray(card.checklist).filter((item) => item.done).length, 0);
    const totalAttachments = cards.reduce((acc, card) => acc + Number(card.attachmentCount || 0), 0);
    const isLoading = COLUMNS_LOADING || CARDS_LOADING;

    const renderCard = (card) => {
        const progress = onChecklistProgress(card.checklist);
        const overdue = onIsOverdue(card, doneColumnIds);
        const reminderDue = onIsReminderDue(card, doneColumnIds);
        return (
            <div className="LStudio__app_trello_card_inner">
                <div className="LStudio__app_trello_card_head">
                    <button type="button" className="LStudio__app_trello_card_title" onClick={() => onOpenCardPreview(card)}>{card.title}</button>
                    <CardActionMenu title="Opciones de tarjeta" size="sm" items={[
                        { key: "view", label: "Ver detalle", icon: <FaEye />, onClick: () => onOpenCardPreview(card) },
                        { key: "edit", label: "Editar tarjeta", icon: <FaEdit />, onClick: () => onOpenEditCard(card) },
                        { key: "favorite", label: card.isFavorite ? "Quitar destacado" : "Destacar", icon: card.isFavorite ? <FaStar /> : <FaRegStar />, onClick: () => onToggleFavoriteCard(card) },
                        { key: "duplicate", label: "Duplicar tarjeta", icon: <FaCopy />, onClick: () => onDuplicateCard(card) },
                        ...(card.url ? [{ key: "url", label: "Abrir enlace", icon: <FaExternalLinkAlt />, onClick: () => window.open(card.url, "_blank", "noopener,noreferrer") }] : []),
                        { divider: true },
                        { key: "archive", label: "Archivar", icon: <FaArchive />, onClick: () => onArchiveCard(card) },
                    ]} />
                </div>
                <div className="LStudio__app_trello_card_badges">
                    {card.isFavorite && <FaStar title="Destacada" />}
                    <Badge tone={PRIORITY_TONE[card.priority] || "neutral"} size="sm">{onLabel(PRIORITIES, card.priority, "Media")}</Badge>
                    {overdue && <Badge tone="danger" size="sm">Vencida</Badge>}
                    {reminderDue && <Badge tone="warning" size="sm"><FaBell /> Recordatorio</Badge>}
                </div>
                {card.description && <p className="LStudio__app_trello_card_description">{card.description}</p>}
                {onAsArray(card.labels).length > 0 && (
                    <div className="LStudio__app_trello_labels">
                        {onAsArray(card.labels).slice(0, 4).map((label) => <span key={label}>{label}</span>)}
                        {onAsArray(card.labels).length > 4 && <span>+{onAsArray(card.labels).length - 4}</span>}
                    </div>
                )}
                <div className="LStudio__app_trello_card_meta">
                    {card.dueDate && <span data-overdue={overdue ? "true" : undefined}><FaCalendarAlt /> {onDateLabel(card.dueDate)}</span>}
                    {progress.total > 0 && <span data-complete={progress.done === progress.total ? "true" : undefined}><FaCheck /> {progress.done}/{progress.total}</span>}
                    {Number(card.attachmentCount || 0) > 0 && <span><FaPaperclip /> {Number(card.attachmentCount || 0)}</span>}
                    {card.estimateMinutes && <span><FaStopwatch /> {onFormatMinutes(card.estimateMinutes)}</span>}
                </div>
            </div>
        );
    };

    const viewProgress = onChecklistProgress(useCardView?.checklist);
    const viewColumn = columns.find((column) => String(column.id) === String(useCardView?.columnId));
    const viewOverdue = useCardView ? onIsOverdue(useCardView, doneColumnIds) : false;

    return (
        <>
            <div className="LStudio__app_panel_bar">
                <ViewToolbar
                    filters={<>
                        <SearchFilter value={useSearch} onChange={setSearch} total={cards.length} matches={visibleCards.length}
                            placeholder="Ej.: Revisar permisos, urgente o API..." />
                        <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                            onClick={() => setAdvancedOpen((current) => !current)}>
                            {advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}
                        </AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen} activeCount={advancedCount}
                        onClear={() => { setPriorityFilter("ALL"); setDueFilter("ALL"); setColumnFilter("ALL"); }}>
                        <FieldSelect id="trello_priority_filter" label="Prioridad" value={usePriorityFilter}
                            onChange={(event) => setPriorityFilter(event.target.value)}
                            options={[{ value: "ALL", label: "Todas las prioridades" }, ...PRIORITIES]} isSearchable={false} />
                        <FieldSelect id="trello_due_filter" label="Vencimiento" value={useDueFilter}
                            onChange={(event) => setDueFilter(event.target.value)}
                            options={[{ value: "ALL", label: "Cualquier vencimiento" }, { value: "OVERDUE", label: "Vencidas" }, { value: "WITH_DATE", label: "Con fecha" }, { value: "NO_DATE", label: "Sin fecha" }]} isSearchable={false} />
                        <FieldSelect id="trello_column_filter" label="Columna" value={useColumnFilter}
                            onChange={(event) => setColumnFilter(event.target.value)}
                            options={[{ value: "ALL", label: "Todas las columnas" }, ...columns.map((column) => ({ value: String(column.id), label: column.name }))]} isSearchable />
                    </AdvancedFilterPanel>}
                    total={visibleCards.length}
                    totalSingular="tarjeta"
                    totalPlural="tarjetas"
                    exportColumns={EXPORT_COLUMNS}
                    exportRows={exportRows}
                    exportName={`trello-${onSafeName(projectName)}`}
                    extraMenuItems={[
                        { key: "markdown", label: "Descargar Markdown", icon: <FaFileAlt />, isDisabled: !cards.length, onClick: onDownloadMarkdown },
                    ]}
                    actions={<>
                        <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                        <AppButton variant="outline" tone="neutral" icon={<FaPlus />} onClick={onOpenCreateColumn}>Nueva columna</AppButton>
                        <AppButton icon={<FaPlus />} isDisabled={!columns.length} onClick={() => onOpenCreateCard(columns[0])}>Nueva tarjeta</AppButton>
                    </>}
                />
            </div>

            {isLoading && <Skeleton variant="box" height={420} />}

            {!isLoading && !columns.length && (
                <EmptyState icon={<FaColumns />} title="Todavía no hay tablero" description="Creá la primera columna para empezar a organizar el proyecto."
                    action={<AppButton icon={<FaPlus />} onClick={onOpenCreateColumn}>Crear columna</AppButton>} />
            )}

            {!isLoading && columns.length > 0 && (
                <div className="LStudio__app_trello_board_shell">
                    <AppKanban
                        board={boardView}
                        columns={kanbanColumns}
                        renderCard={renderCard}
                        renderMoveControl={(card, cols) => <TrelloMoveControl card={card} columns={cols} onMove={onMoveCardFromControl} />}
                        renderColumnDescription={(column) => column.description ? <span className="LStudio__app_kanban__column_description">{column.description}</span> : null}
                        renderColumnActions={(column) => {
                            const raw = column.raw;
                            const index = columns.findIndex((item) => item.id === raw.id);
                            return <CardActionMenu title="Opciones de columna" size="sm" items={[
                                { key: "edit", label: "Editar columna", icon: <FaEdit />, onClick: () => onOpenEditColumn(raw) },
                                { key: "left", label: "Mover a la izquierda", icon: <FaArrowLeft />, isDisabled: index === 0, onClick: () => onMoveColumn(raw, -1) },
                                { key: "right", label: "Mover a la derecha", icon: <FaArrowRight />, isDisabled: index === columns.length - 1, onClick: () => onMoveColumn(raw, 1) },
                                { divider: true },
                                { key: "archive", label: "Archivar columna", icon: <FaArchive />, onClick: () => onArchiveColumn(raw) },
                            ]} />;
                        }}
                        onAddCard={(column) => onOpenCreateCard(column.raw)}
                        emptyLabel="Sin tarjetas" />
                </div>
            )}

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Trello" description={`Resumen del tablero de ${projectName}.`}
                metrics={[
                    { key: "columns", label: "Columnas", value: columns.length, icon: <FaColumns />, tone: "neutral", isLoading },
                    { key: "cards", label: "Tarjetas", value: cards.length, icon: <FaFileAlt />, tone: "accent", isLoading },
                    { key: "done", label: "Completadas", value: doneCards, icon: <FaCheck />, tone: "ok", isLoading },
                    { key: "overdue", label: "Vencidas", value: overdueCards, icon: <FaCalendarAlt />, tone: "warn", isLoading },
                    { key: "checklist", label: "Checklist", value: `${completedChecklist}/${totalChecklist}`, icon: <FaCheckSquare />, tone: "neutral", isLoading },
                    { key: "attachments", label: "Adjuntos", value: totalAttachments, icon: <FaPaperclip />, tone: "neutral", isLoading },
                ]} />

            <AppModal isOpen={useColumnModal} onClose={() => { setColumnModal(false); setColumnEdit(null); }} isDirty={COLUMN_FORM.isDirty}
                size="lg" title={useColumnEdit ? "Editar columna" : "Nueva columna"}
                description="Las columnas representan estados o etapas. Podés crear todas las que necesites y reordenarlas."
                footer={<><AppButton variant="ghost" tone="neutral" onClick={() => setColumnModal(false)}>Cancelar</AppButton><AppButton onClick={COLUMN_FORM.onSubmit} isLoading={useActionApiIsLoading}>{useColumnEdit ? "Guardar cambios" : "Crear columna"}</AppButton></>}>
                <div className="LStudio__app_priority_toggle">
                    <FieldSwitch id="kanban_done_column" label="Columna de completados" text="Las tarjetas dentro de esta columna se cuentan como terminadas" isChecked={!!COLUMN_FORM.values.isDoneColumn} onChange={(event) => COLUMN_FORM.setValue("isDoneColumn", event.target.checked)} />
                </div>
                <FormGrid columns={2}>
                    <FieldInput {...COLUMN_FORM.field("name")} label="Nombre" placeholder="Ej: En revisión" />
                    <FieldInput {...COLUMN_FORM.field("wipLimit")} label="Límite visible (opcional)" type="number" min="1" placeholder="Sin límite" />
                    <FieldTextarea {...COLUMN_FORM.field("description")} label="Descripción" rows={4} span={2} placeholder="Qué representa esta etapa y cuándo mover una tarjeta acá." />
                </FormGrid>
            </AppModal>

            <AppModal isOpen={useCardModal} onClose={onCloseCardEditor} isDirty={CARD_FORM.isDirty || usePendingFiles.length > 0}
                size="xl" title={useCardEdit ? "Editar tarjeta" : "Nueva tarjeta"}
                description="Definí la tarjeta. El checklist se marca únicamente desde la vista previa para separar planificación de ejecución."
                footer={<><AppButton variant="ghost" tone="neutral" onClick={onCloseCardEditor}>Cancelar</AppButton><AppButton onClick={CARD_FORM.onSubmit} isLoading={useSavingCard || useActionApiIsLoading}>{useCardEdit ? "Guardar cambios" : "Crear tarjeta"}</AppButton></>}>
                <div className="LStudio__app_priority_toggle">
                    <FieldSwitch id="kanban_card_favorite" label="Destacada" text="Mantener esta tarjeta visible como prioridad personal" isChecked={!!CARD_FORM.values.isFavorite} onChange={(event) => CARD_FORM.setValue("isFavorite", event.target.checked)} />
                </div>
                <FormGrid columns={2}>
                    <FieldSelect {...CARD_FORM.field("columnId")} label="Columna" options={columnOptions} />
                    <FieldSelect {...CARD_FORM.field("priority")} label="Prioridad" options={PRIORITIES} />
                    <FieldInput {...CARD_FORM.field("title")} label="Título" span={2} placeholder="Ej: Revisar permisos del auditor" />
                    <FieldTextarea {...CARD_FORM.field("description")} label="Descripción" rows={5} span={2} placeholder="Qué hay que hacer, contexto y resultado esperado." />
                    <FieldTags id="kanban_labels" label="Etiquetas" value={onAsArray(CARD_FORM.values.labels)} onChange={(value) => CARD_FORM.setValue("labels", value)} maxTags={12} span={2} placeholder="Escribí y presioná Enter" />
                    <FieldDate id="kanban_start_date" label="Inicio planificado" value={CARD_FORM.values.startDate || null} onChange={(value) => CARD_FORM.setValue("startDate", value)} showTime />
                    <FieldDate id="kanban_due_date" label="Vencimiento" value={CARD_FORM.values.dueDate || null} onChange={(value) => CARD_FORM.setValue("dueDate", value)} showTime />
                    <FieldDate id="kanban_reminder_at" label="Recordatorio personal" value={CARD_FORM.values.reminderAt || null} onChange={(value) => CARD_FORM.setValue("reminderAt", value)} showTime />
                    <FieldInput {...CARD_FORM.field("url")} label="Enlace relacionado" placeholder="https://..." />
                    <FieldInput {...CARD_FORM.field("estimateMinutes")} label="Tiempo estimado (min)" type="number" min="0" placeholder="Ej: 90" />
                    <FieldInput {...CARD_FORM.field("actualMinutes")} label="Tiempo real (min)" type="number" min="0" placeholder="Opcional" />
                    <FieldTextarea {...CARD_FORM.field("notes")} label="Notas personales" rows={4} span={2} placeholder="Recordatorios, decisiones, referencias o información adicional." />
                    <FieldFile id="kanban_pending_files" label="Adjuntos" files={usePendingFiles} onChange={setPendingFiles} maxFiles={12} maxSize={50 * 1024 * 1024} span={2}
                        hint={useCardEdit?.attachmentCount ? `${useCardEdit.attachmentCount} adjunto(s) existente(s). Los nuevos se subirán al guardar.` : "Los archivos se subirán cuando guardes la tarjeta."}
                        dropText="Arrastrá archivos o hacé clic para adjuntarlos" />
                </FormGrid>

                {useCardEdit && (
                    <section className="LStudio__app_trello_edit_attachments">
                        <div className="LStudio__app_trello_preview_section_head">
                            <div>
                                <strong><FaPaperclip /> Adjuntos actuales</strong>
                                <span>Estos archivos ya están guardados en la tarjeta. Podés abrirlos a pantalla completa, descargarlos o archivarlos sin salir de edición.</span>
                            </div>
                            {useCardFiles.length > 1 && (
                                <AppButton size="sm" variant="outline" tone="neutral" icon={<FaDownload />} isLoading={useDownloadingFiles} onClick={onDownloadAllAttachments}>
                                    Descargar todos
                                </AppButton>
                            )}
                        </div>

                        <div className="LStudio__app_trello_attachments LStudio__app_trello_attachments--editor">
                            {useCardFilesLoading && <Skeleton variant="box" height={80} />}
                            {!useCardFilesLoading && !useCardFiles.length && (
                                <span className="LStudio__app_trello_preview_empty">Esta tarjeta todavía no tiene archivos guardados.</span>
                            )}
                            {!useCardFilesLoading && useCardFiles.map((file) => (
                                <div key={file.id} className="LStudio__app_trello_attachment">
                                    <span className="LStudio__app_trello_attachment_icon"><FaFileAlt /></span>
                                    <div className="LStudio__app_trello_attachment_info">
                                        <strong title={file.originalName || file.title}>{file.originalName || file.title}</strong>
                                        <span>{[file.mimetype, onFormatSize(file.size)].filter(Boolean).join(" · ")}</span>
                                    </div>
                                    <div className="LStudio__app_trello_attachment_actions">
                                        <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEye />} aria-label="Vista previa" title="Abrir vista previa" onClick={() => setPreviewFile(file)} />
                                        <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaDownload />} aria-label="Descargar" title="Descargar" onClick={() => onDownloadAttachment(file)} />
                                        <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaArchive />} aria-label="Archivar" title="Archivar" onClick={() => onArchiveAttachment(file)} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <section className="LStudio__app_trello_checklist_editor">
                    <div className="LStudio__app_trello_checklist_head">
                        <div><strong>Checklist · estructura</strong><span>Acá solo organizás los items. Para marcarlos como hechos, abrí la vista previa de la tarjeta.</span></div>
                        <Badge tone="neutral" size="sm">{onChecklistProgress(CARD_FORM.values.checklist).done}/{onChecklistProgress(CARD_FORM.values.checklist).total}</Badge>
                    </div>
                    <div className="LStudio__app_trello_checklist_add">
                        <input value={useChecklistDraft} onChange={(event) => setChecklistDraft(event.target.value)} placeholder="Nuevo item del checklist"
                            onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); onAddChecklistItem(); } }} />
                        <AppButton size="sm" variant="outline" tone="neutral" icon={<FaPlus />} onClick={onAddChecklistItem}>Agregar</AppButton>
                    </div>
                    <div className="LStudio__app_trello_checklist_items">
                        {onAsArray(CARD_FORM.values.checklist).length === 0 && <span className="LStudio__app_trello_checklist_empty">Sin items todavía.</span>}
                        {onAsArray(CARD_FORM.values.checklist).map((item, index) => (
                            <div key={item.id} className="LStudio__app_trello_checklist_item" data-done={item.done ? "true" : undefined}>
                                <span className="LStudio__app_trello_checklist_status" title="El estado se cambia desde la vista previa">{item.done ? <FaCheck /> : <FaRegCircle />}</span>
                                <input className="LStudio__app_trello_checklist_text_input" value={item.text} onChange={(event) => onChangeChecklistText(item.id, event.target.value)} aria-label="Texto del item" />
                                <div className="LStudio__app_trello_checklist_order">
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaArrowUp />} aria-label="Subir item" isDisabled={index === 0} onClick={() => onMoveChecklistItem(item.id, -1)} />
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaArrowDown />} aria-label="Bajar item" isDisabled={index === onAsArray(CARD_FORM.values.checklist).length - 1} onClick={() => onMoveChecklistItem(item.id, 1)} />
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaTrash />} aria-label="Quitar item" onClick={() => onRemoveChecklistItem(item.id)} />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </AppModal>

            <AppModal
                isOpen={!!useCardView}
                onClose={onCloseCardPreview}
                size="xl"
                title={useCardView?.title || "Detalle de tarjeta"}
                description="Vista de ejecución personal: mové la tarjeta, completá el checklist y administrá sus adjuntos sin entrar a edición."
                footer={useCardView ? <>
                    <AppButton variant="ghost" tone="neutral" icon={<FaArchive />} onClick={() => onArchiveCard(useCardView)}>Archivar</AppButton>
                    <AppButton variant="outline" tone="neutral" icon={<FaEdit />} onClick={() => { const card = useCardView; onCloseCardPreview(); onOpenEditCard(card); }}>Editar</AppButton>
                    <AppButton onClick={onCloseCardPreview}>Cerrar</AppButton>
                </> : null}
            >
                {useCardView && (
                    <div className="LStudio__app_trello_preview">
                        <section className="LStudio__app_trello_preview_hero">
                            <div className="LStudio__app_trello_preview_identity">
                                <div className="LStudio__app_trello_preview_badges">
                                    {useCardView.isFavorite && <Badge tone="neutral" size="sm"><FaStar /> Destacada</Badge>}
                                    <Badge tone={PRIORITY_TONE[useCardView.priority] || "neutral"} size="sm">{onLabel(PRIORITIES, useCardView.priority, "Media")}</Badge>
                                    {viewOverdue && <Badge tone="danger" size="sm">Vencida</Badge>}
                                </div>
                                <p>{useCardView.description || "Sin descripción. Podés agregar contexto desde Editar."}</p>
                            </div>
                            <div className="LStudio__app_trello_preview_move">
                                <span>Estado actual</span>
                                <TrelloMoveControl card={useCardView} columns={kanbanColumns} onMove={onMoveCardFromControl} isWide />
                            </div>
                        </section>

                        <div className="LStudio__app_trello_preview_grid">
                            <div className="LStudio__app_trello_preview_main">
                                <section className="LStudio__app_trello_preview_section">
                                    <div className="LStudio__app_trello_preview_section_head">
                                        <div><strong>Checklist</strong><span>Marcá el avance desde acá. En edición los checks están bloqueados a propósito.</span></div>
                                        <Badge tone={viewProgress.total && viewProgress.done === viewProgress.total ? "success" : "neutral"} size="sm">{viewProgress.done}/{viewProgress.total}</Badge>
                                    </div>
                                    {viewProgress.total > 0 && <div className="LStudio__app_trello_progress"><span style={{ width: `${viewProgress.percent}%` }} /></div>}
                                    <div className="LStudio__app_trello_preview_checklist">
                                        {!viewProgress.total && <span className="LStudio__app_trello_preview_empty">Esta tarjeta no tiene checklist.</span>}
                                        {onAsArray(useCardView.checklist).map((item) => (
                                            <button key={item.id} type="button" className="LStudio__app_trello_preview_check" data-done={item.done ? "true" : undefined}
                                                role="checkbox" aria-checked={!!item.done} onClick={() => onTogglePreviewChecklistItem(item.id)}>
                                                <span>{item.done ? <FaCheck /> : null}</span><strong>{item.text}</strong>
                                            </button>
                                        ))}
                                    </div>
                                </section>

                                <section className="LStudio__app_trello_preview_section">
                                    <div className="LStudio__app_trello_preview_section_head">
                                        <div><strong><FaPaperclip /> Adjuntos</strong><span>Archivos privados de esta tarjeta. Podés previsualizarlos, descargarlos o archivarlos.</span></div>
                                        {useCardFiles.length > 1 && <AppButton size="sm" variant="outline" tone="neutral" icon={<FaDownload />} isLoading={useDownloadingFiles} onClick={onDownloadAllAttachments}>Descargar todos</AppButton>}
                                    </div>

                                    <div className="LStudio__app_trello_preview_upload">
                                        <FieldFile id="kanban_preview_files" label="Agregar archivos" files={usePreviewUploadFiles} onChange={setPreviewUploadFiles} maxFiles={12} maxSize={50 * 1024 * 1024} span={2}
                                            dropText="Arrastrá archivos o hacé clic para adjuntar" hint="Se guardan dentro de la tarjeta actual." />
                                        <AppButton size="sm" icon={<FaUpload />} isDisabled={!usePreviewUploadFiles.length} isLoading={usePreviewUploading} onClick={onPreviewUpload}>Subir adjuntos</AppButton>
                                    </div>

                                    <div className="LStudio__app_trello_attachments">
                                        {useCardFilesLoading && <Skeleton variant="box" height={80} />}
                                        {!useCardFilesLoading && !useCardFiles.length && <span className="LStudio__app_trello_preview_empty">Sin archivos adjuntos.</span>}
                                        {!useCardFilesLoading && useCardFiles.map((file) => (
                                            <div key={file.id} className="LStudio__app_trello_attachment">
                                                <span className="LStudio__app_trello_attachment_icon"><FaFileAlt /></span>
                                                <div className="LStudio__app_trello_attachment_info"><strong title={file.originalName || file.title}>{file.originalName || file.title}</strong><span>{[file.mimetype, onFormatSize(file.size)].filter(Boolean).join(" · ")}</span></div>
                                                <div className="LStudio__app_trello_attachment_actions">
                                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEye />} aria-label="Vista previa" title="Vista previa" onClick={() => setPreviewFile(file)} />
                                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaDownload />} aria-label="Descargar" title="Descargar" onClick={() => onDownloadAttachment(file)} />
                                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaArchive />} aria-label="Archivar" title="Archivar" onClick={() => onArchiveAttachment(file)} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                {useCardView.notes && (
                                    <section className="LStudio__app_trello_preview_section">
                                        <div className="LStudio__app_trello_preview_section_head"><div><strong>Notas personales</strong><span>Información de apoyo que no forma parte del checklist.</span></div></div>
                                        <p className="LStudio__app_trello_preview_notes">{useCardView.notes}</p>
                                    </section>
                                )}
                            </div>

                            <aside className="LStudio__app_trello_preview_side">
                                <section className="LStudio__app_trello_preview_section">
                                    <div className="LStudio__app_trello_preview_section_head"><div><strong>Planificación</strong><span>Tu contexto personal para ejecutar esta tarea.</span></div></div>
                                    <div className="LStudio__app_trello_meta_grid">
                                        <div><span>Columna</span><strong>{viewColumn?.name || "-"}</strong></div>
                                        <div><span>Prioridad</span><strong>{onLabel(PRIORITIES, useCardView.priority, "Media")}</strong></div>
                                        <div><span><FaCalendarAlt /> Inicio</span><strong>{onDateTimeLabel(useCardView.startDate) || "Sin definir"}</strong></div>
                                        <div data-overdue={viewOverdue ? "true" : undefined}><span><FaCalendarAlt /> Vencimiento</span><strong>{onDateTimeLabel(useCardView.dueDate) || "Sin definir"}</strong></div>
                                        <div><span><FaBell /> Recordatorio</span><strong>{onDateTimeLabel(useCardView.reminderAt) || "Sin definir"}</strong></div>
                                        <div><span><FaStopwatch /> Estimado</span><strong>{onFormatMinutes(useCardView.estimateMinutes) || "Sin estimar"}</strong></div>
                                        <div><span><FaClock /> Tiempo real</span><strong>{onFormatMinutes(useCardView.actualMinutes) || "Sin registrar"}</strong></div>
                                        <div><span><FaPaperclip /> Adjuntos</span><strong>{useCardFiles.length}</strong></div>
                                    </div>
                                </section>

                                {onAsArray(useCardView.labels).length > 0 && (
                                    <section className="LStudio__app_trello_preview_section">
                                        <div className="LStudio__app_trello_preview_section_head"><div><strong>Etiquetas</strong></div></div>
                                        <div className="LStudio__app_trello_labels LStudio__app_trello_labels--preview">{onAsArray(useCardView.labels).map((label) => <span key={label}>{label}</span>)}</div>
                                    </section>
                                )}

                                {useCardView.url && (
                                    <section className="LStudio__app_trello_preview_section">
                                        <div className="LStudio__app_trello_preview_section_head"><div><strong>Enlace relacionado</strong></div></div>
                                        <AppButton variant="outline" tone="neutral" size="sm" icon={<FaExternalLinkAlt />} isFullWidth onClick={() => window.open(useCardView.url, "_blank", "noopener,noreferrer")}>Abrir enlace</AppButton>
                                    </section>
                                )}

                                <section className="LStudio__app_trello_preview_section">
                                    <div className="LStudio__app_trello_preview_section_head"><div><strong>Registro</strong></div></div>
                                    <div className="LStudio__app_trello_meta_grid">
                                        <div><span>Creada</span><strong>{onDateTimeLabel(useCardView.createdAt) || "-"}</strong></div>
                                        <div><span>Actualizada</span><strong>{onDateTimeLabel(useCardView.updatedAt) || "-"}</strong></div>
                                    </div>
                                </section>
                            </aside>
                        </div>
                    </div>
                )}
            </AppModal>

            <UniversalFilePreviewModal
                file={usePreviewFile}
                isOpen={!!usePreviewFile}
                onClose={() => setPreviewFile(null)}
                loadBlob={onLoadAttachmentBlob}
                onDownload={onDownloadAttachment}
            />
        </>
    );
};
